import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store';

const selectDiscountsState = (state: RootState) => state.discounts;

export const selectAllDiscounts = createSelector([selectDiscountsState], (state) =>
  state.allIds.map((id) => state.byId[id])
);

export const selectDiscountById = (id: string) =>
  createSelector([selectDiscountsState], (state) => state.byId[id]);

export const selectFilteredDiscounts = createSelector([selectDiscountsState], (state) => {
  let discounts = state.allIds.map((id) => state.byId[id]);

  const { type, status, validity, search } = state.filters;

  if (type) {
    discounts = discounts.filter((d) => d.discount_type === type);
  }

  if (status) {
    discounts = discounts.filter((d) => d.status === status);
  }

  if (validity) {
    const now = new Date();
    discounts = discounts.filter((d) => {
      const validFrom = new Date(d.valid_from);
      const validUntil = new Date(d.valid_until);

      if (validity === 'current') {
        return now >= validFrom && now <= validUntil;
      } else if (validity === 'upcoming') {
        return now < validFrom;
      } else if (validity === 'expired') {
        return now > validUntil;
      }
      return true;
    });
  }

  if (search) {
    const searchLower = search.toLowerCase();
    discounts = discounts.filter(
      (d) =>
        d.code.toLowerCase().includes(searchLower) ||
        d.name.toLowerCase().includes(searchLower) ||
        (d.description && d.description.toLowerCase().includes(searchLower))
    );
  }

  return discounts;
});

export const selectActiveDiscounts = createSelector([selectDiscountsState], (state) => {
  const now = new Date();
  return state.allIds
    .map((id) => state.byId[id])
    .filter((d) => {
      const validFrom = new Date(d.valid_from);
      const validUntil = new Date(d.valid_until);
      return d.is_active && now >= validFrom && now <= validUntil;
    });
});

export const selectExpiringDiscounts = createSelector([selectDiscountsState], (state) => {
  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  return state.allIds
    .map((id) => state.byId[id])
    .filter((d) => {
      const validUntil = new Date(d.valid_until);
      return d.is_active && validUntil > now && validUntil <= sevenDaysFromNow;
    });
});

export const selectDiscountsFilters = createSelector(
  [selectDiscountsState],
  (state) => state.filters
);

export const selectSelectedDiscount = createSelector([selectDiscountsState], (state) =>
  state.selectedDiscountId ? state.byId[state.selectedDiscountId] : null
);

export const selectDiscountsLoading = createSelector(
  [selectDiscountsState],
  (state) => state.isLoading
);

export const selectDiscountsError = createSelector([selectDiscountsState], (state) => state.error);
